/**
 * Layer Freshness Registry
 *
 * Weather/model layers (wind, air quality, lightning risk, fire danger)
 * report the valid-time of the data they are currently displaying, and the
 * DataFreshnessChip renders a compact "valid HH:MM" summary on the map.
 *
 * Why this exists: these layers show *forecast model* data. The ingestion
 * pipeline fetches each model run once and then hourly re-publishes the
 * forecast hour that has arrived, so "how old is this?" is the wrong
 * question — "which hour is this valid for?" is the right one, and it's
 * what this registry surfaces to users.
 */

import { useSyncExternalStore } from 'react';

export interface LayerFreshness {
  /** Short human label, e.g. "Wind". */
  label: string;
  /** ISO time the displayed data is valid for (model valid time). */
  validTime: string;
  /** ISO time the underlying model data was fetched (optional). */
  fetchedAt?: string;
}

const entries = new Map<string, LayerFreshness>();
const listeners = new Set<() => void>();
// Immutable snapshot handed to React; replaced on every mutation so
// useSyncExternalStore sees a new reference only when something changed.
let snapshot: ReadonlyMap<string, LayerFreshness> = new Map();

function emit(): void {
  snapshot = new Map(entries);
  listeners.forEach((listener) => listener());
}

/** Report (or update) the freshness of a layer's displayed data. */
export function reportLayerFreshness(key: string, info: LayerFreshness): void {
  const existing = entries.get(key);
  if (
    existing &&
    existing.validTime === info.validTime &&
    existing.fetchedAt === info.fetchedAt &&
    existing.label === info.label
  ) {
    return; // no change, don't wake subscribers
  }
  entries.set(key, info);
  emit();
}

/** Remove a layer's entry (call when the layer is disabled/unmounted). */
export function clearLayerFreshness(key: string): void {
  if (entries.delete(key)) {
    emit();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** React hook: the current set of reported layers, re-rendering on change. */
export function useLayerFreshness(): ReadonlyMap<string, LayerFreshness> {
  return useSyncExternalStore(subscribe, () => snapshot);
}
